import React, { useEffect, useMemo, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useSetCmpEmailSubscriberTargetMutation } from "../../../../../graphql/types";
import {
  SectionSaveReset,
  settingsInputClassName,
  settingsSectionCardClassName,
  settingsSectionDescriptionClassName,
  settingsSectionTitleClassName,
} from "../common";

export type EmailSubscriberTargetValue = {
  calendarYear: number;
  target: number;
  updatedAtUtcMilli?: number | null;
};

const formatLastSavedLabel = (target: EmailSubscriberTargetValue) => {
  if (!target.updatedAtUtcMilli) {
    return `Current target for ${target.calendarYear}`;
  }

  return `Last Saved ${new Intl.DateTimeFormat("en-US", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(target.updatedAtUtcMilli))}`;
};

const getTargetValidationError = (value: string) => {
  const trimmedValue = value.trim();

  if (!/^\d+$/.test(trimmedValue)) {
    return "Enter a whole number that is zero or greater.";
  }

  const parsedValue = Number(trimmedValue);
  if (!Number.isSafeInteger(parsedValue)) {
    return "Enter a smaller whole number.";
  }

  return null;
};

const EmailSubscriberTargetSection = ({
  chamberId,
  initialTarget,
  onDirtyChange,
}: {
  chamberId: string;
  initialTarget: EmailSubscriberTargetValue;
  onDirtyChange: (isDirty: boolean) => void;
}) => {
  const {
    calendarYear: initialCalendarYear,
    target: initialTargetValue,
    updatedAtUtcMilli: initialUpdatedAtUtcMilli,
  } = initialTarget;
  const [savedTarget, setSavedTarget] = useState(initialTarget);
  const [draftTarget, setDraftTarget] = useState(String(initialTargetValue));
  const [errorText, setErrorText] = useState<string | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [setSubscriberTarget, { loading: saving }] =
    useSetCmpEmailSubscriberTargetMutation();

  useEffect(() => {
    setSavedTarget({
      calendarYear: initialCalendarYear,
      target: initialTargetValue,
      updatedAtUtcMilli: initialUpdatedAtUtcMilli,
    });
    setDraftTarget(String(initialTargetValue));
    setErrorText(null);
    setStatusText(null);
  }, [
    initialCalendarYear,
    initialTargetValue,
    initialUpdatedAtUtcMilli,
  ]);

  const isDirty = useMemo(
    () => draftTarget.trim() !== String(savedTarget.target),
    [draftTarget, savedTarget.target]
  );

  useEffect(() => {
    onDirtyChange(isDirty);

    return () => onDirtyChange(false);
  }, [isDirty, onDirtyChange]);

  const handleSave = async () => {
    const validationError = getTargetValidationError(draftTarget);
    if (validationError) {
      setErrorText(validationError);
      setStatusText(null);
      return;
    }

    try {
      setErrorText(null);
      setStatusText(null);
      const result = await setSubscriberTarget({
        variables: {
          input: {
            chamberId,
            calendarYear: savedTarget.calendarYear,
            target: Number(draftTarget.trim()),
          },
        },
      });
      const updatedTarget = result.data?.setCmpEmailSubscriberTarget.target;

      if (!updatedTarget) {
        throw new Error("The email subscriber target update returned no data.");
      }

      const nextTarget: EmailSubscriberTargetValue = {
        calendarYear: updatedTarget.calendarYear,
        target: updatedTarget.target,
        updatedAtUtcMilli: updatedTarget.updatedAtUtcMilli,
      };
      setSavedTarget(nextTarget);
      setDraftTarget(String(nextTarget.target));
      setStatusText("Email subscriber target saved.");
    } catch (error: any) {
      setErrorText(
        error?.message || "Unable to save the email subscriber target."
      );
    }
  };

  const handleReset = () => {
    setDraftTarget(String(savedTarget.target));
    setErrorText(null);
    setStatusText(null);
  };

  return (
    <section className={settingsSectionCardClassName}>
      <h2 className={settingsSectionTitleClassName}>
        Set Your Email Subscribers Yearly Target
      </h2>
      <p className={settingsSectionDescriptionClassName}>
        Define your annual goal for email subscribers. This target will be used
        to track your progress on the analytics dashboard.
      </p>

      <div className="mt-7 max-w-3xl">
        <label
          htmlFor="email-subscriber-yearly-target"
          className="block text-base font-bold text-black"
        >
          Yearly Target
        </label>
        <p className="mt-2 text-sm leading-6 text-black">
          Enter your email subscribers goal for the selected year.
          <br />
          This will be your target for Jan 1 – Dec 31, {savedTarget.calendarYear}.
        </p>
        <div className="relative mt-3">
          <EmailOutlinedIcon
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            id="email-subscriber-yearly-target"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={draftTarget}
            onChange={(event) => {
              setDraftTarget(event.target.value);
              setErrorText(null);
              setStatusText(null);
            }}
            aria-describedby={`email-subscriber-yearly-target-description${
              errorText ? " email-subscriber-yearly-target-error" : ""
            }`}
            aria-invalid={Boolean(errorText)}
            className={`${settingsInputClassName} w-full pl-12`}
          />
        </div>
        <p
          id="email-subscriber-yearly-target-description"
          className="mt-2 text-xs leading-5 text-gray-600"
        >
          The target is initialized once from your active membership size and
          stays fixed until you update it here.
        </p>
        {errorText ? (
          <p id="email-subscriber-yearly-target-error" className="sr-only">
            {errorText}
          </p>
        ) : null}
      </div>

      <SectionSaveReset
        isDirty={isDirty}
        saving={saving}
        lastSavedLabel={formatLastSavedLabel(savedTarget)}
        onSave={handleSave}
        onReset={handleReset}
        statusText={statusText}
        errorText={errorText}
      />
    </section>
  );
};

export default EmailSubscriberTargetSection;
